import React, { useEffect, useState, forwardRef, useImperativeHandle, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Card, Form, Row, Col } from 'react-bootstrap';
import { AiOutlinePlus, AiOutlineFilter, AiOutlineSearch, AiOutlineClear } from 'react-icons/ai';
import { FiMoreVertical, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { FaCheck, FaTimes, FaSortUp, FaSortDown } from 'react-icons/fa';
import Dropdown from 'react-bootstrap/Dropdown';
import moment from 'moment';
import Select from 'react-select';

import Api, { exceptionNotificationAPI } from '../../services/api';
import TableLoading from '../TableLoading';
import { Table, PaginationContainer } from './styles';
import { formSelectStyles } from '../../components/FormInput/formSelectStyles';

/**
 * Enhanced Generic Table Component with Filters, Sorting, and Pagination
 * 
 * @param {string} title - Table title
 * @param {string} apiEndpoint - API endpoint for fetching data
 * @param {string} toggleActiveEndpoint - API endpoint for toggling active status
 * @param {string} toggleDeletedEndpoint - API endpoint for toggling deleted status
 * @param {string} newRoute - Route for creating new records
 * @param {array} columns - Array of column definitions
 * @param {array} filterConfig - Array of filter field configurations
 * @param {array} actions - Array of action button configurations
 * @param {string} rowKey - Unique key for each row
 * @param {number} initialPageSize - Initial page size
 * @param {object} initialFilters - Filter values applied on first load and after clearing
 * @param {boolean} fitToContainer - Keep the table within its container and wrap long content
 * @param {boolean} enableFilters - Enable filter functionality
 * @param {boolean} showAddButton - Show add new button
 * @param {ReactNode} loadingIcon - Custom loading icon
 */
const TableCrudEnhanced = forwardRef(
    (
        {
            title,
            apiEndpoint,
            toggleActiveEndpoint,
            toggleDeletedEndpoint,
            newRoute,
            columns = [],
            filterConfig = [],
            actions = [],
            rowKey = 'id',
            initialPageSize = 10,
            initialFilters = {},
            fitToContainer = false,
            enableFilters = false,
            showAddButton = false,
            loadingIcon = <TableLoading />,
            ...restProps
        },
        ref
    ) => {
        const navigate = useNavigate();
        const filtersRef = useRef({ ...initialFilters });
        
        // State management
        const [data, setData] = useState([]);
        const [loading, setLoading] = useState(false);
        const [filters, setFilters] = useState(() => ({ ...initialFilters }));
        const [showFilters, setShowFilters] = useState(false);
        const [sorter, setSorter] = useState({ field: null, order: null });
        const [pagination, setPagination] = useState({
            current: 1,
            pageSize: initialPageSize,
            total: 0,
            pageSizeOptions: ['5', '10', '50', '100', '1000'],
        });
        const [userObject] = useState(JSON.parse(localStorage.getItem('@Thrive:user')));

        // Sync filters ref with filters state
        useEffect(() => {
            filtersRef.current = filters;
        }, [filters]);

        // Fetch data from API
        const fetchData = useCallback(async (page, pageSize, sorterField, sortOrder) => {
            setLoading(true);
            try {
                const response = await Api.post(apiEndpoint, {
                    page,
                    pageSize,
                    sorterField,
                    sortOrder,
                    reportYearID: userObject.selectedReportYearID,
                    ...filtersRef.current
                });

                setData(response.data.data.data);
                setPagination(prev => ({
                    ...prev,
                    current: page,
                    pageSize: pageSize,
                    total: response.data.data.pagination.totalRecords,
                }));
            } catch (error) {
                exceptionNotificationAPI(error);
                setData([]);
            } finally {
                setLoading(false);
            }
        }, [apiEndpoint, userObject.selectedReportYearID]);

        // Load data
        const loadData = useCallback(() => {
            fetchData(pagination.current, pagination.pageSize, sorter.field, sorter.order);
        }, [fetchData, pagination.current, pagination.pageSize, sorter.field, sorter.order]);

        // Expose reload function
        useImperativeHandle(ref, () => ({
            reloadTable() {
                loadData();
            },
        }), [loadData]);

        // Initial load
        useEffect(() => {
            loadData();
        }, []);

        // Handle sorting
        const handleSort = useCallback((columnName) => {
            let direction = 'asc';
            if (sorter.field === columnName && sorter.order === 'asc') {
                direction = 'desc';
            } else if (sorter.field === columnName && sorter.order === 'desc') {
                direction = null;
            }
            
            setSorter({ field: columnName, order: direction });
            fetchData(pagination.current, pagination.pageSize, columnName, direction);
        }, [sorter, fetchData, pagination.current, pagination.pageSize]);

        // Handle pagination change
        const handlePageChange = useCallback((newPage) => {
            fetchData(newPage, pagination.pageSize, sorter.field, sorter.order);
        }, [fetchData, pagination.pageSize, sorter.field, sorter.order]);

        // Handle page size change
        const handlePageSizeChange = useCallback((newPageSize) => {
            fetchData(1, parseInt(newPageSize), sorter.field, sorter.order);
        }, [fetchData, sorter.field, sorter.order]);

        // Handle filter submit
        const handleFilter = useCallback(() => {
            fetchData(1, pagination.pageSize, sorter.field, sorter.order);
        }, [fetchData, pagination.pageSize, sorter.field, sorter.order]);

        // Handle clear filters
        const handleClearFilters = useCallback(() => {
            const defaultFilters = { ...initialFilters };
            setFilters(defaultFilters);
            filtersRef.current = defaultFilters;
            fetchData(1, pagination.pageSize, sorter.field, sorter.order);
        }, [fetchData, initialFilters, pagination.pageSize, sorter.field, sorter.order]);

        // Handle delete
       
        // Handle toggle active
        const handleToggleActive = useCallback(async (record) => {
            if (!toggleActiveEndpoint) return;

            setLoading(true);
            try {
                const id = record[rowKey];
                const currentStatus = record.isActive;
                await Api.post(`${toggleActiveEndpoint}?id=${id}&isActive=${!currentStatus}`);
                loadData();
            } catch (error) {
                exceptionNotificationAPI(error);
            } finally {
                setLoading(false);
            }
        }, [toggleActiveEndpoint, rowKey, loadData]);

        // Handle toggle deleted
        const handleToggleDeleted = useCallback(async (record) => {
            if (!toggleDeletedEndpoint) return;

            setLoading(true);
            try {
                const id = record[rowKey];
                const currentStatus = record.isDeleted;
                await Api.post(`${toggleDeletedEndpoint}?id=${id}&isDeleted=${!currentStatus}`);
                loadData();
            } catch (error) {
                exceptionNotificationAPI(error);
            } finally {
                setLoading(false);
            }
        }, [toggleDeletedEndpoint, rowKey, loadData]);

        // Get value by path (for nested properties)
        const getValueByPath = useCallback((obj, path) => {
            if (!path || !obj) return null;
            if (path.indexOf(".") > -1) {
                const fragments = path.split('.');
                let current = obj;
                for (const fragment of fragments) {
                    if (current && current[fragment] !== undefined) {
                        current = current[fragment];
                    } else {
                        return null;
                    }
                }
                return current;
            }
            return obj[path];
        }, []);

        // Render cell
        const renderCell = useCallback((item, column, index) => {
            const path = column.dataIndex || column.name;
            const value = getValueByPath(item, path);
            
            if (column.render) {
                return <td key={index}>{column.render(value, item, index, handleToggleActive, handleToggleDeleted)}</td>;
            }
            
            if (typeof value === "boolean") {
                return (
                    <td key={index}>
                        {value ? 
                            <FaCheck color="#28a745" size={14} /> : 
                            <FaTimes color="#dc3545" size={14} />
                        }
                    </td>
                );
            }
            
            if (column.type === "date") {
                return <td key={index}>{moment(value).format("DD-MMM-yyyy")}</td>
            }
            
            return <td key={index}>{value || '-'}</td>;
        }, [getValueByPath, handleToggleActive, handleToggleDeleted]);

        // Render filter field
        const renderFilterField = useCallback((filter, index) => {
            const value = filters[filter.name] ?? '';
            
            switch (filter.type) {
                case 'text':
                    return (
                        <Col xs={24} sm={12} md={6} lg={4} key={index}>
                            <Form.Group controlId={filter.name}>
                                <Form.Label>{filter.label}</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder={filter.placeholder}
                                    size="sm"
                                    value={value}
                                    onChange={(e) => setFilters({ ...filters, [filter.name]: e.target.value })}
                                />
                            </Form.Group>
                        </Col>
                    );
                case 'select':
                    return (
                        <Col xs={24} sm={12} md={6} lg={4} key={index}>
                            <Form.Group controlId={filter.name}>
                                <Form.Label>{filter.label}</Form.Label>
                                <Form.Select
                                    size="sm"
                                    value={value}
                                    onChange={(e) => {
                                        const selectedOption = filter.options?.find(
                                            option => String(option.value) === e.target.value
                                        );
                                        setFilters({
                                            ...filters,
                                            [filter.name]: selectedOption?.value ?? e.target.value
                                        });
                                    }}
                                >
                                    {filter.options?.map((opt, idx) => (
                                        <option key={idx} value={opt.value}>{opt.label}</option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </Col>
                    );
                case 'date':
                    return (
                        <Col xs={24} sm={12} md={6} lg={4} key={index}>
                            <Form.Group controlId={filter.name}>
                                <Form.Label>{filter.label}</Form.Label>
                                <Form.Control
                                    type="date"
                                    size="sm"
                                    value={value}
                                    onChange={(e) => setFilters({ ...filters, [filter.name]: e.target.value })}
                                />
                            </Form.Group>
                        </Col>
                    );
                default:
                    return null;
            }
        }, [filters]);

        // Pagination component
        const totalPages = Math.ceil(pagination.total / pagination.pageSize);
        const startItem = (pagination.current - 1) * pagination.pageSize + 1;
        const endItem = Math.min(pagination.current * pagination.pageSize, pagination.total);

        return (
            <div className="container-fluid" >
                <Row gutter={[8, 8]}>
                    <Col span={24}>
                        <Card variant="borderless" style={{ padding: '2rem' }}>
                            {/* Header */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' , justifyContent: 'space-between' }}>
                                <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#262626' }}>
                                    {title}
                                </h2>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                {showAddButton && newRoute && (
                                    <Button
                                        variant="primary"
                                        onClick={() => navigate(newRoute)}
                                        disabled={loading}
                                        size="sm"
                                        style={{
                                            padding: '0.6rem 1.4rem',
                                            fontSize: '13px',
                                            fontWeight: 600,
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px',
                                        }}
                                    >
                                        <AiOutlinePlus size={16} /> Add New
                                    </Button>
                                )}
                                {enableFilters && filterConfig.length > 0 && (
                                    <Button
                                        variant={showFilters ? "secondary" : "outline-secondary"}
                                        onClick={() => setShowFilters(!showFilters)}
                                        size="sm"
                                        style={{
                                            padding: '0.6rem 1.4rem',
                                            fontSize: '13px',
                                            fontWeight: 600,
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px',
                                        }}
                                        title={showFilters ? "Hide Filters" : "Show Filters"}
                                    >
                                        <AiOutlineFilter size={16}/> {showFilters ? "Hide Filters" : "Show Filters"}
                                    </Button>
                                )}
                                </div>
                               
                            </div>

                            {/* Filters */}
                            {enableFilters && showFilters && filterConfig.length > 0 && (
                                <div style={{
                                    margin: '12px 0',
                                    padding: '12px',
                                    backgroundColor: '#fafafa',
                                    border: '1px solid #f0f0f0',
                                    borderRadius: '6px'
                                }}>
                                    <Form
                                        layout="vertical"
                                        onSubmit={(e) => {
                                            e.preventDefault();
                                            handleFilter();
                                        }}
                                        style={{ marginBottom: 0 }}
                                    >
                                        <Row gutter={[12, 6]} align="bottom">
                                            {filterConfig.map((filter, idx) => renderFilterField(filter, idx))}
                                            
                                            <Col xs={24} sm={12} md={6} lg={4}>
                                                <Form.Group>
                                                    <Form.Label style={{ visibility: 'hidden' }}>Actions</Form.Label>
                                                    <div style={{ display: 'flex', gap: '8px' }}>
                                                        <Button
                                                            variant="primary"
                                                            type="button"
                                                            size="sm"
                                                            disabled={loading}
                                                            onClick={handleFilter}
                                                        >
                                                            <AiOutlineSearch /> Filter
                                                        </Button>
                                                        <Button
                                                            variant="outline-secondary"
                                                            size="sm"
                                                            onClick={handleClearFilters}
                                                        >
                                                            <AiOutlineClear /> Clear
                                                        </Button>
                                                    </div>
                                                </Form.Group>
                                            </Col>
                                        </Row>
                                    </Form>
                                </div>
                            )}

                            {/* Table */}
                            <div style={{ padding: '16px 0' }}>
                                {loading ? (
                                    loadingIcon
                                ) : (
                                    <Table $fitToContainer={fitToContainer}>
                                        <thead className="sticky-top">
                                            <tr>
                                                {columns.map((c, i) => {
                                                    const path = c.dataIndex || c.name;
                                                    return (
                                                        <th 
                                                            key={i}
                                                            onClick={() => c.sortable && handleSort(path)}
                                                            style={{ 
                                                                cursor: c.sortable ? 'pointer' : 'default',
                                                                width: c.width || 'auto'
                                                            }}
                                                        >
                                                            {c.title || c.label}
                                                            {c.sortable && (
                                                                <span className="sort-indicator">
                                                                    {sorter.field === path && sorter.order === 'asc' && <FaSortUp size={12} />}
                                                                    {sorter.field === path && sorter.order === 'desc' && <FaSortDown size={12} />}
                                                                </span>
                                                            )}
                                                        </th>
                                                    );
                                                })}
                                                <th
                                                    style={{ width: fitToContainer ? '4%' : '1%' }}
                                                    aria-label="Actions"
                                                />
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {data.length > 0 ? (
                                                data.map((item, i) => (
                                                    <tr key={item[rowKey] || i}>
                                                        {columns.map((c, x) => renderCell(item, c, x))}
                                                        <td>
                                                            <Dropdown>
                                                                <Dropdown.Toggle disabled={loading}>
                                                                    <FiMoreVertical size={16} />
                                                                </Dropdown.Toggle>
                                                                <Dropdown.Menu>
                                                                    {actions.map((action, idx) => (
                                                                        <Dropdown.Item
                                                                            key={idx}
                                                                            onClick={() => {
                                                                                
                                                                                    action.onClick(item);
                                                                                
                                                                            }}
                                                                            className={action.danger ? 'text-danger' : ''}
                                                                        >
                                                                            {action.label}
                                                                        </Dropdown.Item>
                                                                    ))}
                                                                </Dropdown.Menu>
                                                            </Dropdown>
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td className="no-records" colSpan={columns.length + 1}>
                                                        No records found
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </Table>
                                )}
                                
                                {/* Pagination */}
                                {!loading && (
                                    <PaginationContainer>
                                        <div className="pagination-info">
                                            Showing {startItem}-{endItem} of {pagination.total} items
                                        </div>
                                        <div className="pagination-controls">
                                            <div className="page-size-selector">
                                                <label>Items per page:</label>
                                                <Select
                                                    value={{ value: pagination.pageSize, label: String(pagination.pageSize) }}
                                                    onChange={(opt) => handlePageSizeChange(opt?.value)}
                                                    options={pagination.pageSizeOptions.map(size => ({ value: size, label: String(size) }))}
                                                    isDisabled={loading}
                                                    isSearchable={false}
                                                    styles={{
                                                        ...formSelectStyles,
                                                        control: (base, state) => ({ ...formSelectStyles.control(base, state), minWidth: 80 }),
                                                    }}
                                                    classNamePrefix="tablecrud-pagesize"
                                                />
                                            </div>
                                            <div className="page-navigation">
                                                <button
                                                    onClick={() => handlePageChange(pagination.current - 1)}
                                                    disabled={loading || pagination.current <= 1}
                                                    className="btn btn-outline-secondary"
                                                >
                                                    <FiChevronLeft />
                                                </button>
                                                <span className="page-info">
                                                    Page {pagination.current} of {totalPages}
                                                </span>
                                                <button
                                                    onClick={() => handlePageChange(pagination.current + 1)}
                                                    disabled={loading || pagination.current >= totalPages}
                                                    className="btn btn-outline-secondary"
                                                >
                                                    <FiChevronRight />
                                                </button>
                                            </div>
                                        </div>
                                    </PaginationContainer>
                                )}
                            </div>
                        </Card>
                    </Col>
                </Row>
            </div>
        );
    }
);

TableCrudEnhanced.displayName = 'TableCrudEnhanced';

export default TableCrudEnhanced;
